import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminShell, Card, Tile, Notice, ErrorNote, Loading, EmptyState, confirmDanger, ago, useAdmin } from "./AdminShell";
import {
  adminBackupStatus, adminBackupEveryone, adminPlayerBackups, adminMakeBackup, adminRestorePreview, adminRestoreBackup,
  adminEditBackup, adminDeleteBackup, adminDownloadBackup, adminLoadBackupIntoMine, adminImportBackupFromLive,
  adminListPlayers, type AdminBackup, type AdminBackupStatus, type AdminRestorePreview, type AdminPlayerRow,
} from "../../sim/admin";

/**
 * ADMIN — SAVE BACKUPS (server/backups.js). Copies of every player's save, so an update or a database change that
 * damages saves can be undone, one player at a time. Written for someone who opens it twice a year, in a hurry:
 *   • the page says, in sentences and four big numbers, that backups are running and when the last ones were made;
 *   • find a player → their backups, newest first, each with the numbers that matter (coins, Petals, levels);
 *   • ↩ Restore… shows exactly what would change BEFORE anything happens — and what is kept as it is now (gifts,
 *     purchases, the Club) — and a copy of their save right now is made first, so a restore can itself be undone;
 *   • 📌 Make a copy now keeps one until you delete it; ⬇ Download saves it as a file; 🧪 puts a copy into your own
 *     account on the TEST server, to play their save without touching theirs.
 * The same list is the player page's 🛟 Backups tab (`PlayerBackups`).
 */
export function AdminBackups() {
  const [params, setParams] = useSearchParams();
  const player = params.get("player");
  const importId = Number(params.get("import")) || null;
  const pick = (id: string | null) => setParams(id ? { player: id } : {});
  return (
    <AdminShell section="backups" title="Backups" subtitle="Copies of every player's save — made automatically, and whenever you want one. Restore any of them in two clicks.">
      {importId && <ImportFromLive id={importId} onDone={(pid) => pick(pid)} />}
      <StatusCard />
      <FindPlayer selected={player} onPick={pick} />
      {player && <PlayerBackups playerId={player} />}
      <RecentCopies onPick={pick} />
      <HowItWorks />
    </AdminShell>
  );
}

// ---- formatting -------------------------------------------------------------------------------------------------

const WHEN = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long", year: "numeric" });
const n = (v: number) => Math.round(v).toLocaleString();
function when(iso: string | number): string {
  const t = typeof iso === "number" ? iso : Date.parse(iso);
  return Number.isFinite(t) ? `${WHEN.format(t)} · ${ago(t)}` : "—";
}
function mb(bytes: number): string {
  return bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : bytes >= 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}
const errText = (e: unknown) => String((e as Error)?.message || e);

// ---- the system at a glance -------------------------------------------------------------------------------------

function StatusCard() {
  const [st, setSt] = useState<AdminBackupStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(() => { adminBackupStatus().then(setSt).catch((e) => setErr(errText(e))); }, []);
  useEffect(load, [load]);
  if (err) return <ErrorNote error={err} onRetry={load} />;
  if (!st) return <Loading what="Loading backups" />;
  const s = st.status;
  const failed = [s.daily, s.update, s.everyone].filter((r) => r && r.error) as Array<{ label?: string; error?: string; at: number }>;
  const healthy = st.ready && !failed.length && !!s.daily && Date.now() - s.daily.at < 36 * 3_600_000;
  async function runNow() {
    if (!confirmDanger("Back up everyone now?", "Every player whose save changed since their last backup gets a fresh copy. It takes a few seconds and doesn't affect anyone playing.")) return;
    setBusy(true); setMsg(null);
    try {
      const r = await adminBackupEveryone();
      setMsg({ ok: true, text: r.count ? `Done — ${n(r.count)} save${r.count === 1 ? "" : "s"} copied.` : "Done — every save already had an up-to-date backup." });
      load();
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  }
  return (
    <Card title={healthy ? "Backups are running" : st.ready ? "Backups need a look" : "Backups start with the next update"} icon={healthy ? "✅" : "🛟"}
      tone={healthy ? "ok" : "warn"}
      actions={<button className="site-btn primary sm" disabled={busy || !st.ready} onClick={() => void runNow()} data-testid="backup-everyone">🛟 Back up everyone now</button>}>
      <p className="bk-lead">
        Every player's save is copied <b>once a day</b> and <b>right before every update</b> — so if an update or a
        database change ever damages saves, you can put any player back the way they were. A copy is only made when a
        save has changed, so quiet players cost nothing.
      </p>
      {!st.ready && <Notice>The backups table is created by the update that brought this page — once it's deployed, the first backup runs straight away.</Notice>}
      {failed.map((f, i) => <Notice key={i}>{f.label || "A backup"} failed {ago(f.at)}: {f.error}</Notice>)}
      {msg && <Notice ok={msg.ok}>{msg.text}</Notice>}
      <div className="adm-tiles bk-tiles">
        <Tile label="Last daily backup" value={s.daily ? ago(s.daily.at) : "not yet"} sub={s.daily ? `${n(s.daily.count)} saves copied` : "runs once a day"} tone={s.daily && !s.daily.error ? "ok" : "warn"} />
        <Tile label="Before the last update" value={s.update ? ago(s.update.at) : "not yet"} sub={s.update ? `${n(s.update.count)} saves copied` : "runs when an update starts"} />
        <Tile label="Players covered" value={`${n(st.totals.players)}`} sub={st.uncovered ? `${n(st.uncovered)} with no backup yet` : `every player with a garden (${n(st.savers)})`} tone={st.uncovered ? "warn" : "ok"} />
        <Tile label="Backups stored" value={n(st.totals.backups)} sub={mb(st.totals.bytes)} />
      </div>
    </Card>
  );
}

// ---- find a player ----------------------------------------------------------------------------------------------

function FindPlayer({ selected, onPick }: { selected: string | null; onPick: (id: string | null) => void }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<AdminPlayerRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return; }
    const t = setTimeout(() => {
      adminListPlayers({ q: q.trim(), limit: 8 }).then((r) => setRows(r.players)).catch((e) => setErr(errText(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Card title="Find a player" icon="🔎" actions={selected ? <button className="site-btn ghost sm" onClick={() => onPick(null)}>✕ Close their backups</button> : undefined}>
      <input className="adm-input bk-search" placeholder="Name, handle, email or player id…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="backup-search" />
      {err && <ErrorNote error={err} />}
      {rows && rows.length === 0 && <p className="adm-muted">Nobody matches “{q}”.</p>}
      {rows && rows.length > 0 && (
        <ul className="bk-results">
          {rows.map((p) => (
            <li key={p.playerId}>
              <button className={`bk-result${selected === p.playerId ? " on" : ""}`} onClick={() => { onPick(p.playerId); setRows(null); setQ(""); }}>
                <b>{p.name || p.handle || "(no name)"}</b> <span className="adm-muted">{p.email || p.playerId}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---- one player's backups ---------------------------------------------------------------------------------------

/** One player's backups, newest first — the Backups page's player view, and the player page's 🛟 Backups tab. */
export function PlayerBackups({ playerId, onRestored }: { playerId: string; onRestored?: () => void }) {
  const { info } = useAdmin();
  const [list, setList] = useState<AdminBackup[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);        // the "📌 Make a copy now" note, while open
  const [restoring, setRestoring] = useState<AdminBackup | null>(null);
  const load = useCallback(() => {
    setErr(null);
    adminPlayerBackups(playerId).then((r) => setList(r.backups)).catch((e) => setErr(errText(e)));
  }, [playerId]);
  useEffect(load, [load]);
  const staging = info.env === "staging";
  const name = list?.[0]?.playerName || "this player";

  async function run(what: () => Promise<string>) {
    setBusy(true); setMsg(null);
    try { setMsg({ ok: true, text: await what() }); load(); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  }
  const makeCopy = () => run(async () => {
    await adminMakeBackup(playerId, note || "");
    setNote(null);
    return "Copy made — it's at the top of the list, and it's kept until you delete it.";
  });

  return (
    <Card title={`Backups of ${name}'s save`} icon="🛟" className="bk-player"
      actions={<button className="site-btn primary sm" disabled={busy} onClick={() => setNote(note === null ? "" : null)} data-testid="backup-make">📌 Make a copy now</button>}>
      <p className="adm-muted">
        Restoring a backup puts their game back to that moment — and keeps anything they've bought or been given since.
        Their save as it is right now is copied first, so a restore can always be undone.
      </p>
      {note !== null && (
        <div className="bk-make">
          <input className="adm-input" autoFocus placeholder="A note for yourself (optional) — e.g. “before testing the pond fix”" value={note}
            onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void makeCopy(); }} />
          <button className="site-btn primary sm" disabled={busy} onClick={() => void makeCopy()}>Save the copy</button>
          <button className="site-btn ghost sm" onClick={() => setNote(null)}>Cancel</button>
        </div>
      )}
      {msg && <Notice ok={msg.ok}>{msg.text}</Notice>}
      {restoring && <RestorePanel b={restoring} onCancel={() => setRestoring(null)}
        onDone={(text) => { setRestoring(null); setMsg({ ok: true, text }); load(); onRestored?.(); }} />}
      {err && <ErrorNote error={err} onRetry={load} />}
      {!list && !err && <Loading what="Loading their backups" />}
      {list && list.length === 0 && (
        <EmptyState icon="🛟">No backups of their save yet. The daily backup will make one if they've played — or press <b>📌 Make a copy now</b>.</EmptyState>
      )}
      {list && list.length > 0 && (
        <ul className="bk-list" data-testid="backup-list">
          {list.map((b) => (
            <li key={b.id} className={`bk-row${restoring?.id === b.id ? " on" : ""}`} data-backup={b.id}>
              <div className="bk-icon" aria-hidden>{b.icon}</div>
              <div className="bk-main">
                <div className="bk-title"><b>{b.kindLabel}</b>{b.label && <span className="bk-note"> — {b.label}</span>}</div>
                <div className="bk-when">{when(b.createdAt)}</div>
                <div className="bk-nums">
                  <span>🪙 {n(b.coins)}</span><span>🌸 {n(b.petals)}</span>
                  <span title="Gardening · Fishing · Insects levels">🌱 Lv {b.levels.garden} · 🎣 {b.levels.fish} · 🦋 {b.levels.bug}</span>
                  <span>🏅 {n(b.achievements)}</span><span>🪴 {n(b.plantings)} growing</span>
                </div>
                <div className="bk-keep">{b.keep ? "📌 Kept until you delete it" : b.keptUntil ? `Kept until ${DAY.format(Date.parse(b.keptUntil))}` : "Kept until you delete it"}</div>
              </div>
              <div className="bk-actions">
                <button className="site-btn primary sm" disabled={busy} onClick={() => { setMsg(null); setRestoring(b); }} data-testid="backup-restore">↩ Restore…</button>
                <button className="site-btn ghost sm" disabled={busy} onClick={() => void run(async () => { await adminDownloadBackup(b.id); return "Downloaded."; })}>⬇ Download</button>
                {staging
                  ? <button className="site-btn ghost sm" disabled={busy} title="Put this save into YOUR account on this test server, to play it. Your own test save is copied first."
                      onClick={() => {
                        if (!confirmDanger("Load this save into your own test account?", "Your own test save is copied first (it appears in your backups), then replaced with this one. The player's save isn't touched.")) return;
                        void run(async () => { await adminLoadBackupIntoMine(b.id); return "Loaded into your test account — open the game to play it."; });
                      }}>🧪 Load into my account</button>
                  : info.stagingUrl
                    ? <a className="site-btn ghost sm" href={`${info.stagingUrl.replace(/\/$/, "")}/admin/backups?import=${b.id}`} target="_blank" rel="noreferrer"
                        title="Open the TEST server's admin site and copy this backup there, so you can play their save without touching theirs">🧪 Try on the test server</a>
                    : null}
                <button className="site-btn ghost sm" disabled={busy} title={b.keep ? "Let it be tidied away when it's old" : "Never tidy this one away"}
                  onClick={() => void run(async () => { await adminEditBackup(b.id, { keep: !b.keep }); return b.keep ? "It'll be tidied away when it's old." : "Kept — it won't be tidied away."; })}>
                  {b.keep ? "📌 Unkeep" : "📌 Keep"}</button>
                <button className="site-btn ghost sm danger" disabled={busy} title="Delete this backup"
                  onClick={() => { if (confirmDanger("Delete this backup?", "This can't be undone. Their save isn't affected.")) void run(async () => { await adminDeleteBackup(b.id); return "Deleted."; }); }}>🗑</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** ↩ THE RESTORE — what changes (now → after), what is kept as it is now, then one button. */
function RestorePanel({ b, onCancel, onDone }: { b: AdminBackup; onCancel: () => void; onDone: (text: string) => void }) {
  const [p, setP] = useState<AdminRestorePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminRestorePreview(b.id).then(setP).catch((e) => setErr(errText(e))); }, [b.id]);
  const changed = useMemo(() => (p ? p.rows.filter((r) => r.changed).length : 0), [p]);
  async function go() {
    setBusy(true);
    try {
      const r = await adminRestoreBackup(b.id);
      onDone(`Restored to ${WHEN.format(Date.parse(b.createdAt))}. `
        + (r.live === "reloaded" ? "They were playing — their game has reloaded onto it. " : "")
        + (r.safetyBackupId ? "Their save from just before is now the top backup (“Before a restore”) — restore that to undo this." : ""));
    } catch (e) { setErr(errText(e)); setBusy(false); }
  }
  return (
    <Card title="Restore this backup?" icon="↩️" tone="warn" className="bk-restore">
      <p className="bk-lead">Their save goes back to <b>{WHEN.format(Date.parse(b.createdAt))}</b> ({ago(Date.parse(b.createdAt))}) — {b.kindLabel.toLowerCase()}{b.label ? `, “${b.label}”` : ""}.</p>
      {err && <ErrorNote error={err} />}
      {!p && !err && <Loading what="Working out what would change" />}
      {p && (
        <>
          <table className="bk-compare" data-testid="restore-compare">
            <thead><tr><th>What</th><th>Now</th><th>After restoring</th></tr></thead>
            <tbody>
              {p.rows.map((r) => (
                <tr key={r.label} className={r.changed ? "changed" : ""}>
                  <td>{r.label}</td><td>{n(r.now)}</td><td>{r.changed ? <b>{n(r.then)} {r.then > r.now ? "▲" : "▼"}</b> : n(r.then)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {changed === 0 && <p className="adm-muted">None of these numbers change — the backup and their save look the same here.</p>}
          <div className="bk-kept">
            <b>Kept exactly as it is now:</b>
            <ul>{p.carried.map((l) => <li key={l}>{l}</li>)}</ul>
          </div>
          <p className="bk-safe">✅ A copy of their save right now is made first, so you can undo this by restoring it.</p>
          {p.online && <p className="bk-online">🟢 They're playing right now — their game will reload onto the restored save.</p>}
          {!p.hasCurrent && <p className="adm-muted">They have no garden saved right now, so this simply puts the backup back.</p>}
        </>
      )}
      <div className="bk-confirm">
        <button className="site-btn primary" disabled={!p || busy} onClick={() => void go()} data-testid="restore-confirm">{busy ? "Restoring…" : "↩ Restore it"}</button>
        <button className="site-btn ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </Card>
  );
}

// ---- recent copies, the test-server import, and the explainer ----------------------------------------------------

function RecentCopies({ onPick }: { onPick: (id: string) => void }) {
  const [st, setSt] = useState<AdminBackupStatus | null>(null);
  useEffect(() => { adminBackupStatus().then(setSt).catch(() => {}); }, []);
  if (!st || !st.recent.length) return null;
  return (
    <Card title="Copies and restores lately" icon="🗂️">
      <ul className="bk-recent">
        {st.recent.map((b) => (
          <li key={b.id}>
            <span aria-hidden>{b.icon}</span> <b>{b.kindLabel}</b> — <button className="bk-link" onClick={() => onPick(b.playerId)}>{b.playerName || b.playerId}</button>
            {b.label && <span className="adm-muted"> · {b.label}</span>} <span className="adm-muted">· {when(b.createdAt)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ImportFromLive({ id, onDone }: { id: number; onDone: (playerId: string) => void }) {
  const { info } = useAdmin();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (info.env !== "staging") return <Notice>Open this link on the TEST server's admin site — that's where a copy from live goes.</Notice>;
  async function go(load: boolean) {
    setBusy(true); setMsg(null);
    try {
      const r = await adminImportBackupFromLive(id, load);
      setMsg({ ok: true, text: load ? "Copied here and loaded into your test account — open the game to play it." : "Copied here — it's in their backups below." });
      onDone(r.playerId);
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  }
  return (
    <Card title="Copy a backup from the live server" icon="📥" tone="ks">
      <p className="bk-lead">You opened this from the live admin site. Copy that player's backup onto this <b>test server</b> — their real save on live isn't touched.</p>
      {msg && <Notice ok={msg.ok}>{msg.text}</Notice>}
      <div className="bk-confirm">
        <button className="site-btn primary" disabled={busy} onClick={() => void go(true)}>🧪 Copy it and load it into my test account</button>
        <button className="site-btn ghost" disabled={busy} onClick={() => void go(false)}>📥 Just copy it here</button>
      </div>
    </Card>
  );
}

function HowItWorks() {
  return (
    <Card title="How backups work" icon="❓">
      <ul className="bk-how">
        <li><b>🌙 Every day</b> and <b>🚀 right before every update</b> (a new version of the game, or a database change), every save that has changed since its last backup is copied. Daily copies are kept 2 weeks, before-update copies a month — and a player's newest backup is never tidied away.</li>
        <li><b>📌 Make a copy now</b> on any player keeps a copy until you delete it. <b>📌 Keep</b> stops any backup being tidied away.</li>
        <li><b>↩ Restore…</b> shows what would change first. Their play goes back to that moment; anything they bought, or were given by a code or by you, since then stays theirs; their Garden Club stays as it is. Their save right now is copied first (“Before a restore”), so you can undo it.</li>
        <li><b>🧪 To test with a player's save</b> without touching theirs: on the live site use <b>Try on the test server</b>; on the test site use <b>Load into my account</b> — your own test save is copied first.</li>
        <li><b>⬇ Download</b> saves a backup as a file, to keep somewhere else.</li>
      </ul>
      <p className="adm-muted">Every restore, copy and delete is in the <Link to="/admin/audit">Audit log</Link>.</p>
    </Card>
  );
}
